import { useMemo } from 'react';
import {
  BusinessCollectionCompType,
  IObjektVy,
} from '../../../../../Types/ObjektVy';
import { collectionCompToObjektVy } from '../../../_Common/Components/BusinessCollectionComponents/BusinessCollectionUtils';
import BusinessDataForm from '../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import { IClientBusinessModel } from '../../../../../Types/ClientModelTypes';
import clientAvenyModel from '../../../../../Model/clientAvenyModel';
import ObjektVy from '../../../_Common/Components/BusinessCollectionComponents/ObjektVy/ObjektVy';
import ObjektVySida from '../../../_Common/Components/BusinessCollectionComponents/ObjektVySida/ObjektVySida';
import { ObjektVyKort } from '../../../_Common/Components/BusinessCollectionComponents/ObjektVyKort/ObjektVyKort';
import Attribut from '../../../_Common/Components/BusinessCollectionComponents/Attribut/Attribut';
import AttributGrupp from '../../../_Common/Components/BusinessCollectionComponents/AttributGrupp/AttributGrupp';
import AttributeHeader from '../../../../../Components/Attributes/AttributeHeader/AttributeHeader';
import query from './Queries/LeverantorfakturaQuery.gql';
import {
  LeverantorfakturaQuery,
  LeverantorfakturaQuery_node_TEDLRLeverantorfaktura,
  LeverantorfakturaQueryVariables,
} from './Queries/_QueryTypes/LeverantorfakturaQuery';
import { Attestanter } from './Components/Attestanter';
import { FakturaBilder } from './FakturaBilder';
import {
  AssociationList,
  IAssociationListConfig,
} from '../../../_Common/Components/BusinessCollectionComponents/AssociationList/AssociationList';
import { vyDefToCaptionedComp } from '../../../../../Utils/ObjektVyCaptionUtils';
import ObjektVyKolumner, {
  ObjektVyKolumn,
} from '../../../_Common/Components/BusinessCollectionComponents/ObjektVyKolumner/ObjektVyKolumner';
import { definedSelectedId } from '../../../../../Components/MasterDetail/MasterDetailUtils';
import { useSelectedDetailQuery } from '../../../../../Components/QueryComponents/SelectedDetailQuery/useSelectedDetailQuery';
import { AttestMeddelandeView } from './Components/AttestMeddelande/AttestMeddelande';

const leverantorsfakturaModel: IClientBusinessModel =
  clientAvenyModel.LR.BusinessModels.LRLeverantorfaktura;

const levfaktAttestantModel: IClientBusinessModel =
  clientAvenyModel.LR.BusinessModels.LRLevfaktAttestant;

const Leverantorsfaktura: BusinessCollectionCompType = props => {
  const id = props.id ?? '';
  const { nodeData, loading: nodeLoading } = useSelectedDetailQuery<
    LeverantorfakturaQuery,
    LeverantorfakturaQuery_node_TEDLRLeverantorfaktura,
    LeverantorfakturaQueryVariables
  >(query, props.id);

  const attestantVyer = useMemo(() => {
    return [Attestanter];
  }, []);

  const FakturaVy = useMemo(() => {
    return vyDefToCaptionedComp(levfaktAttestantModel)(FakturaBilder);
  }, []);

  const fakturahandelserConfig = useMemo<IAssociationListConfig>(() => {
    return {
      defaultVisningstyp: 'lista',
      hideToolbar: false,
      inCard: true,
      businessCollectionMenuItems: [],
      customDefaultColumns: [
        { attributeDef: 'Handelse', caption: 'Händelse', header: true },
        { attributeDef: 'Bokfverifikationer.DATUM', caption: 'Bokf datum' },
        { attributeDef: 'Bokfverifikationer.VER_NR', caption: 'Ver nr' },
        { attributeDef: 'BELOPP', caption: 'Belopp' },
      ],
      customSOLabel: 'Fakturahändelse',
    };
  }, []);

  const betalningarConfig = useMemo<IAssociationListConfig>(() => {
    return {
      defaultVisningstyp: 'lista',
      hideToolbar: false,
      inCard: true,
      businessCollectionMenuItems: [],
      customDefaultColumns: [
        {
          attributeDef: 'UTBET_DATUM',
          caption: 'Utbetalningsdatum',
          header: true,
        },
        { attributeDef: 'STATUS', caption: 'Status' },
        { attributeDef: 'BELOPP', caption: 'Belopp' },
      ],
    };
  }, []);

  const dokumentConfig = useMemo<IAssociationListConfig>(() => {
    return {
      defaultVisningstyp: 'lista',
      hideToolbar: false,
      inCard: true,
      businessCollectionMenuItems: [],
    };
  }, []);

  return (
    <BusinessDataForm<LeverantorfakturaQuery_node_TEDLRLeverantorfaktura>
      data={nodeData}
      disableEditMode={true}
      model={leverantorsfakturaModel}
      fullScreen={props.config?.fullPage ?? false}
    >
      <ObjektVy
        nodeLoading={nodeLoading}
        sidaVidSida={true}
        sideBySideWidths={[7, 5]}
        //editImplemented
      >
        <ObjektVySida
          caption="Fakturabilder"
          sidNr={1}
          collectionName={leverantorsfakturaModel.CollectionName}
          fillPageSmall={true}
        >
          <FakturaVy
            id={nodeData?.id ?? null}
            key={`${FakturaBilder.type}-${FakturaBilder.id}-${props.id}`}
          />
        </ObjektVySida>
        <ObjektVySida
          caption="Leverantörsfaktura"
          sidNr={2}
          collectionName={leverantorsfakturaModel.CollectionName}
        >
          <ObjektVyKolumner>
            <ObjektVyKolumn fullPage={props.config?.fullPage ?? false}>
              <AttributGrupp readOnly>
                <AttributeHeader attributeGroups={['Leverantor.Namn']} />
                <Attribut name="FAKTURA_NR" size="small" />
                <Attribut name="Fakturabelopp2" size="small" />
                <Attribut name="FAKTURADATUM" size="small" />
                <Attribut name="MomsBelopp" size="small" />
                <Attribut name="FORFALLO_DATUM" size="small" />
                <Attribut name="FakturaSaldo" size="small" />
                <Attribut name="Detaljstatus" size="small" />
                <Attribut name="SLUTBETALD" size="small" />
              </AttributGrupp>
              <ObjektVyKort
                readOnly
                vyer={attestantVyer}
                caption="Attestanter"
                bmod={levfaktAttestantModel}
                id={nodeData?.id ?? null}
                config={{
                  parents: [{ id: nodeData?.id ?? null, associationName: '' }],
                }}
              />
              <ObjektVyKort caption="Fakturahändelser">
                {id && (
                  <AssociationList
                    collectionName={leverantorsfakturaModel.CollectionName}
                    ownId={definedSelectedId(id)}
                    config={fakturahandelserConfig}
                    associationName="Fakturahandelser"
                  />
                )}
              </ObjektVyKort>
            </ObjektVyKolumn>
            <ObjektVyKolumn fullPage={props.config?.fullPage ?? false}>
              <ObjektVyKort caption="Betalningar">
                {id && (
                  <AssociationList
                    collectionName={leverantorsfakturaModel.CollectionName}
                    ownId={definedSelectedId(id)}
                    config={betalningarConfig}
                    associationName="Betalningar"
                  />
                )}
              </ObjektVyKort>
              <ObjektVyKort caption="Meddelanden">
                {id && (
                  <AttestMeddelandeView
                    id={nodeData?.id ?? null}
                    messages={
                      nodeData?._Associations?.Attestmeddelande_All ?? []
                    }
                  />
                )}
              </ObjektVyKort>
              <ObjektVyKort caption="Dokument">
                {id && (
                  <AssociationList
                    collectionName={leverantorsfakturaModel.CollectionName}
                    ownId={definedSelectedId(id)}
                    config={dokumentConfig}
                    associationName="Dokument"
                  />
                )}
              </ObjektVyKort>
            </ObjektVyKolumn>
          </ObjektVyKolumner>
        </ObjektVySida>
      </ObjektVy>
    </BusinessDataForm>
  );
};

const leverantorsfakturaObjektVy = collectionCompToObjektVy(Leverantorsfaktura);

export const LeverantorsfakturaVyer: IObjektVy[] = [leverantorsfakturaObjektVy];
